import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import {
  ClipboardList,
  BarChart3,
  MapPin,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  Users,
} from 'lucide-react';
import AntaresLogo from '../../components/AntaresLogo';

const navItems = [
  { to: '/admin/pesquisas', icon: ClipboardList, label: 'Pesquisas', description: 'Criar e gerenciar' },
  { to: '/admin/registros', icon: BarChart3, label: 'Registros', description: 'Ver respostas' },
  { to: '/admin/leads', icon: Users, label: 'Leads WhatsApp', description: 'Contatos capturados' },
];

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();

  const currentPage = navItems.find((n) => location.pathname.startsWith(n.to));

  return (
    <div className="flex min-h-screen bg-[#07051a] text-white">
      {/* Sidebar */}
      <aside
        className={`${
          collapsed ? 'w-[64px]' : 'w-[220px]'
        } shrink-0 bg-[#0a0820] border-r border-white/8 flex flex-col transition-all duration-300 relative`}
      >
        {/* Logo */}
        <div className={`px-4 py-5 border-b border-white/8 flex items-center gap-3 overflow-hidden`}>
          <div className="shrink-0">
            <AntaresLogo className="h-8 w-8" />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-xs font-bold text-white truncate">Antares GTA</p>
              <p className="text-[10px] text-purple-400/70 truncate">Painel Admin</p>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 p-2.5 space-y-1">
          {!collapsed && (
            <p className="text-[10px] font-semibold text-white/20 uppercase tracking-widest px-3 pb-2 pt-1">
              Menu
            </p>
          )}
          {navItems.map(({ to, icon: Icon, label, description }) => (
            <NavLink
              key={to}
              to={to}
              title={collapsed ? label : undefined}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-white/40 border border-transparent hover:bg-white/5 hover:text-white/80 transition-all group"
              style={({ isActive }: any) =>
                isActive
                  ? {
                      background: 'rgba(124,58,237,0.18)',
                      color: '#c4b5fd',
                      borderColor: 'rgba(124,58,237,0.3)',
                    }
                  : {}
              }
            >
              <Icon size={16} className="shrink-0" />
              {!collapsed && (
                <div className="min-w-0">
                  <p className="text-sm font-semibold leading-none">{label}</p>
                  <p className="text-[10px] text-white/25 mt-0.5 leading-none group-hover:text-white/40 transition">
                    {description}
                  </p>
                </div>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-2.5 border-t border-white/8 space-y-2">
          <Link
            to="/pesquisa-campo"
            title={collapsed ? 'Coletar Dados' : undefined}
            className={`flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-violet-500 hover:from-purple-500 hover:to-violet-400 text-white text-xs font-semibold transition-all shadow-lg shadow-purple-600/20 ${
              collapsed ? 'px-0' : 'px-3'
            }`}
          >
            <MapPin size={14} className="shrink-0" />
            {!collapsed && 'Coletar Dados'}
          </Link>

          {/* Collapse toggle */}
          <button
            onClick={() => setCollapsed((v) => !v)}
            className="flex items-center justify-center w-full py-2 rounded-xl text-white/20 hover:text-white/50 hover:bg-white/5 transition text-xs gap-1.5"
          >
            {collapsed ? <ChevronRight size={14} /> : <><ChevronLeft size={14} /><span>Recolher</span></>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="h-14 border-b border-white/8 bg-[#07051a] flex items-center px-6 gap-3 shrink-0">
          <div className="flex items-center gap-2 text-white/20 text-xs">
            <LayoutDashboard size={13} />
            <span>Admin</span>
            {currentPage && (
              <>
                <span>/</span>
                <span className="text-white/50 font-medium">{currentPage.label}</span>
              </>
            )}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
